import "./style.scss";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import CustomSwitch from "../../../../CustomUtils/CustomSwitch/CustomSwitch";
import { Editor } from "@tinymce/tinymce-react";
import type { Editor as TinyMCEEditor } from "tinymce";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import {
  TINYMCE_EDITOR_CONTENT_STYLE,
  TINYMCE_EDITOR_HEIGHT,
  TINYMCE_FONT_SIZE_FORMATS,
} from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import {
  buildIntakeOptionsWithStored,
  getIntakeDisplayLabel,
  resolveDefaultIntakeValue,
} from "@/src/utils/intakeCalc";
import { STANDARD_DEGREE_LEVEL_VALUES } from "@/src/utils/admissionOffice/buildDegreeSelectOptions";
import { MEDIUM_OF_INSTRUCTION_LABELS, normalizeMediumOfInstructionValue } from "@/src/constants/AdmissionOffice/mediumOfInstructionOptions";
import { ADMISSION_SEMESTER_LABELS, normalizeAdmissionSemesterValue } from "@/src/constants/AdmissionOffice/admissionSemesterOptions";
import {
  MALTA_OFFERED_COURSES_BY_LABEL,
  MALTA_OFFERED_COURSE_LABELS,
} from "@/src/constants/AdmissionOffice/Malta/maltaOfferedCoursesConfig";
import {
  applyMaltaOfferedCourseSelection,
  applyMaltaOfferedCourseCertificationEqualization,
  applyMaltaOfferedCoursePreparatoryDepartment,
  clearMaltaOfferedCourseSelection,
  isMaltaPreparatoryOfferedCourse,
} from "@/src/utils/admissionOffice/malta/applyMaltaOfferedCourseSelection";
import { formatMaltaMoneyValue } from "@/src/utils/admissionOffice/malta/buildMaltaFullTimeAdmissionBody";
import {
  applyShengenDefaultFormDefaults,
  FORM_OF_EDUCATION_DEFAULT,
  SHENGEN_FORM_OF_EDUCATION_LABELS,
} from "@/src/constants/AdmissionOffice/Shengen/shengenDefaultAdmissionConfig";
import {
  buildShengenDefaultAdmissionBody,
  calcShengenDefaultFirstYearTotal,
} from "@/src/utils/admissionOffice/shengen/buildShengenDefaultAdmissionBody";
import { applyMaltaFormOfEducationUnselectedDefaults } from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";
import {
  FORM_OF_EDUCATION_DISTANCE,
  normalizeFormOfEducationValue,
} from "@/src/constants/AdmissionOffice/Malta/maltaFormOfEducationOptions";
import MaltaFullTimeFeeField from "../../AdmissionLetterBasic/AOPConfig/MaltaFullTimeFeeField";
import AOPIdSourceRow, { AOP_ID_SOURCE_DEFAULT_VALUES } from "@/src/components/AdmissionsOfficePage/shared/AOPIdSourceRow";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { AOPShengenAdmissionData } from "@/src/types/admissionOffice";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { SHENGEN_ADMISSION_SIGNATURE_LABELS } from "@/src/constants/AdmissionOffice/Shengen/AOShengenEnConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionTaskPrefill, type AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { useAOPIdSourceFields } from "@/src/hooks/useAOPIdSourceFields";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import { useSyncWithQRPreview } from "@/src/hooks/useSyncWithQRPreview";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import {
  admissionLetterTaskPrefillMapping,
  syncCourseAndNationalityAutocompletes,
  syncFormOfEducationAutocomplete,
  syncMediumOfInstructionAutocomplete,
} from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { joinSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  fullName: string;
  fullNameNat?: string;
  dateOfBirth: string;
  nationality: string;
  formOfEducation?: string;
  admissionSemester?: string;
  intake?: string;
  category?: string;
  offeredCourse?: string;
  course: string;
  selectedCourse: string;
  degree?: string;
  mediumOfInstruction: string;
  duration: string;
  tuitionFee?: string;
  medicalInsurance?: string;
  residencePermit?: string;
  otherExpenses?: string;
  acommodation?: string;
  admissionFee?: string;
  transfer?: string;
  completeOneYear?: string;
  certificationEqualization?: string;
  preparatoryDepartment?: string;
  tuitionFeeFlag?: boolean;
  tuitionFeeVisible?: boolean;
  acommodationVisible?: boolean;
  medicalInsuranceVisible?: boolean;
  residencePermitVisible?: boolean;
  admissionFeeVisible?: boolean;
  otherExpensesVisible?: boolean;
  transferVisible?: boolean;
  certificationEqualizationVisible?: boolean;
  preparatoryDepartmentVisible?: boolean;
  completeOneYearVisible?: boolean;
  showSecondYear?: boolean;
  signature: string;
  coursePrefix: string;
  withQR?: boolean;
  task_id?: string;
  student_id?: string;
  fieldEmail?: boolean;
  userEmail?: string;
}

interface AOPConfigProps {
  dataForGeneration: AOPShengenAdmissionData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPShengenAdmissionData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPShengenAdmissionData>>;
  setWithQRForPreview: React.Dispatch<React.SetStateAction<boolean>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDate = (value: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
};

const AOPConfig: FC<AOPConfigProps> = ({
  dataForGeneration,
  setDataForGeneration,
  setBodyForPrev,
  setWithQRForPreview,
}) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      currentDate: formatDateForInput(today),
      fullName: "",
      fullNameNat: "",
      dateOfBirth: "",
      nationality: "",
      formOfEducation: "",
      offeredCourse: "",
      course: "",
      selectedCourse: "",
      degree: "",
      mediumOfInstruction: "",
      duration: "",
      signature: "Admission Letter",
      withQR: true,
      ...AOP_ID_SOURCE_DEFAULT_VALUES,
      tuitionFeeFlag: false,
      tuitionFeeVisible: true,
      acommodationVisible: true,
      medicalInsuranceVisible: true,
      residencePermitVisible: true,
      admissionFeeVisible: true,
      otherExpensesVisible: true,
      transferVisible: true,
      certificationEqualizationVisible: true,
      preparatoryDepartmentVisible: false,
      completeOneYearVisible: true,
      showSecondYear: true,
    },
  });

  const dispatch = useDispatch();
  const [content, setContent] = useState("");
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const isSyncingFromInputs = useRef(false);
  const hasInitializedIntakeRef = useRef(false);
  const prevFormOfEducationRef = useRef<string | undefined>(undefined);
  const preserveAdmissionSemesterFromTaskRef = useRef(false);
  const applyIdSourceLockRef = useRef<(payload: AdmissionTaskPrefillPayload) => void>(() => {});
  const admissionEditorRef = useRef<TinyMCEEditor | null>(null);

  const generatedDoc = useAdmissionGeneratedDocumentActions({
    getValues,
    setValue: (name, value) => setValue(name as keyof IAOPFormValues, value as never),
    studentIdFormField: "student_id",
    documentType: "admission",
  });

  const offeredCourse = watch("offeredCourse");
  const formOfEducation = watch("formOfEducation");
  const intake = watch("intake");
  const degree = watch("degree");
  const mediumOfInstruction = watch("mediumOfInstruction");
  const admissionSemester = watch("admissionSemester");
  const admissionSemesterDisplay = normalizeAdmissionSemesterValue(admissionSemester);
  const fieldEmail = watch("fieldEmail");
  const userEmail = watch("userEmail");
  const tuitionFee = watch("tuitionFee");
  const medicalInsurance = watch("medicalInsurance");
  const residencePermit = watch("residencePermit");
  const otherExpenses = watch("otherExpenses");
  const acommodation = watch("acommodation");
  const admissionFee = watch("admissionFee");
  const transfer = watch("transfer");
  const completeOneYear = watch("completeOneYear");
  const certificationEqualization = watch("certificationEqualization");
  const preparatoryDepartment = watch("preparatoryDepartment");
  const tuitionFeeVisible = watch("tuitionFeeVisible");
  const acommodationVisible = watch("acommodationVisible");
  const medicalInsuranceVisible = watch("medicalInsuranceVisible");
  const residencePermitVisible = watch("residencePermitVisible");
  const admissionFeeVisible = watch("admissionFeeVisible");
  const otherExpensesVisible = watch("otherExpensesVisible");
  const transferVisible = watch("transferVisible");
  const certificationEqualizationVisible = watch("certificationEqualizationVisible");
  const preparatoryDepartmentVisible = watch("preparatoryDepartmentVisible");
  const withQR = watch("withQR");

  const isDefaultForm = (formOfEducation ?? "").trim() === FORM_OF_EDUCATION_DEFAULT;
  const isPreparatoryDepartmentActive = useMemo(() => {
    const entry = offeredCourse ? MALTA_OFFERED_COURSES_BY_LABEL[offeredCourse] : undefined;
    return !!entry && isMaltaPreparatoryOfferedCourse(entry);
  }, [offeredCourse]);

  useSyncWithQRPreview(withQR, setWithQRForPreview);

  const signatureAuto = useAutocomplete(SHENGEN_ADMISSION_SIGNATURE_LABELS);
  const formOfEducationAuto = useAutocomplete([...SHENGEN_FORM_OF_EDUCATION_LABELS]);
  const maltaOfferedCourseAuto = useAutocomplete(MALTA_OFFERED_COURSE_LABELS);
  const degreeAuto = useAutocomplete(STANDARD_DEGREE_LEVEL_VALUES);
  const intakeLabels = useMemo(
    () => buildIntakeOptionsWithStored(intake).map((option) => option.label),
    [intake],
  );
  const intakeAuto = useAutocomplete(intakeLabels);
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);
  const mediumOfInstructionAuto = useAutocomplete(MEDIUM_OF_INSTRUCTION_LABELS);
  const admissionSemesterAuto = useAutocomplete(ADMISSION_SEMESTER_LABELS);

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country: "shengen",
    documentSlug: "admission",
    mapping: admissionLetterTaskPrefillMapping,
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, maltaOfferedCourseAuto, nationalitiesAuto);
      syncFormOfEducationAutocomplete(payload, formOfEducationAuto);
      syncMediumOfInstructionAutocomplete(payload, mediumOfInstructionAuto);
      preserveAdmissionSemesterFromTaskRef.current = Boolean(
        typeof payload.admissionSemester === "string" && payload.admissionSemester.trim(),
      );
      if (typeof payload.offeredCourse === "string" && payload.offeredCourse.trim()) {
        maltaOfferedCourseAuto.onSelect(payload.offeredCourse);
      }
      if (typeof payload.tuitionFeeFlag === "boolean") {
        setValue("tuitionFeeFlag", payload.tuitionFeeFlag, { shouldDirty: false });
        formBaseline.tuitionFeeFlag = String(payload.tuitionFeeFlag);
      } else {
        formBaseline.tuitionFeeFlag = "false";
      }

      const normalizedFormOfEducation = normalizeFormOfEducationValue(
        typeof payload.formOfEducation === "string" ? payload.formOfEducation : "",
      );
      if (normalizedFormOfEducation === FORM_OF_EDUCATION_DISTANCE || normalizedFormOfEducation === FORM_OF_EDUCATION_DEFAULT) {
        setValue("formOfEducation", FORM_OF_EDUCATION_DEFAULT, { shouldDirty: false });
        formOfEducationAuto.onSelect(FORM_OF_EDUCATION_DEFAULT);
        formBaseline.formOfEducation = FORM_OF_EDUCATION_DEFAULT;
        applyShengenDefaultFormDefaults((name, value, options) => {
          setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
        });
      } else {
        setValue("formOfEducation", "", { shouldDirty: false });
        formOfEducationAuto.onSelect("");
        formBaseline.formOfEducation = "";
      }

      applyIdSourceLockRef.current(payload);
    },
  });

  const idSource = useAOPIdSourceFields<IAOPFormValues>({
    setValue,
    getValues,
    applyTaskPrefill,
    lastPrefilledTaskIdRef,
    fieldEmail: !!fieldEmail,
    userEmail,
  });
  applyIdSourceLockRef.current = idSource.applyIdSourceLockFromPrefill;

  useEffect(() => {
    const resolved = (formOfEducation ?? "").trim();
    if (resolved && formOfEducationAuto.query !== resolved) {
      formOfEducationAuto.onSelect(resolved);
    } else if (!resolved && formOfEducationAuto.query) {
      formOfEducationAuto.onSelect("");
    }
  }, [formOfEducation]);

  useEffect(() => {
    const previousFormOfEducation = prevFormOfEducationRef.current;
    prevFormOfEducationRef.current = formOfEducation;

    if (!formOfEducation) {
      if (previousFormOfEducation) {
        applyMaltaFormOfEducationUnselectedDefaults((name, value, options) => {
          setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
        });
        setValue("offeredCourse", "", { shouldDirty: false });
        maltaOfferedCourseAuto.onSelect("");
        clearMaltaOfferedCourseSelection(setValue);
      }
      setContent("");
      return;
    }

    if (formOfEducation === FORM_OF_EDUCATION_DEFAULT) {
      applyShengenDefaultFormDefaults((name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
    }
  }, [formOfEducation, setValue]);

  useEffect(() => {
    const resolved = (degree ?? "").trim();
    if (resolved && degreeAuto.query !== resolved) {
      degreeAuto.onSelect(resolved);
    } else if (!resolved && degreeAuto.query) {
      degreeAuto.onSelect("");
    }
  }, [degree]);

  useEffect(() => {
    const resolved = normalizeMediumOfInstructionValue(mediumOfInstruction);
    if (resolved && mediumOfInstructionAuto.query !== resolved) {
      mediumOfInstructionAuto.onSelect(resolved);
    } else if (!resolved && mediumOfInstructionAuto.query) {
      mediumOfInstructionAuto.onSelect("");
    }
  }, [mediumOfInstruction]);

  useEffect(() => {
    const trimmed = (intake ?? "").trim();
    if (!trimmed) {
      if (!hasInitializedIntakeRef.current) {
        hasInitializedIntakeRef.current = true;
        const defaultLabel = getIntakeDisplayLabel(resolveDefaultIntakeValue());
        setValue("intake", defaultLabel, { shouldDirty: false });
        intakeAuto.onSelect(defaultLabel);
      } else if (intakeAuto.query) {
        intakeAuto.onSelect("");
      }
      return;
    }

    hasInitializedIntakeRef.current = true;
    const displayLabel = getIntakeDisplayLabel(trimmed, buildIntakeOptionsWithStored(trimmed));
    if (displayLabel && intakeAuto.query !== displayLabel) {
      intakeAuto.onSelect(displayLabel);
    }
  }, [intake, setValue]);

  useEffect(() => {
    if (!isDefaultForm) return;

    if (!offeredCourse) {
      applyMaltaOfferedCourseCertificationEqualization(undefined, (name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
      applyMaltaOfferedCoursePreparatoryDepartment(undefined, (name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
      return;
    }

    const entry = MALTA_OFFERED_COURSES_BY_LABEL[offeredCourse];
    if (entry) {
      applyMaltaOfferedCourseSelection(
        entry,
        (name, value, options) => {
          setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
        },
        { updateAdmissionSemester: !preserveAdmissionSemesterFromTaskRef.current },
      );
    }

    preserveAdmissionSemesterFromTaskRef.current = false;
  }, [isDefaultForm, offeredCourse, setValue]);

  useEffect(() => {
    if (!isDefaultForm) return;

    const total = calcShengenDefaultFirstYearTotal(
      {
        tuitionFee,
        admissionFee,
        otherExpenses,
        transfer,
        certificationEqualization,
        preparatoryDepartment,
      },
      {
        tuitionFee: !!tuitionFeeVisible,
        admissionFee: !!admissionFeeVisible,
        otherExpenses: !!otherExpensesVisible,
        transfer: !!transferVisible,
        certificationEqualization: !!certificationEqualizationVisible,
        preparatoryDepartment: !!preparatoryDepartmentVisible,
      },
    );

    if (!total) return;

    const formattedTotal = formatMaltaMoneyValue(total);
    const nextCompleteOneYear = formattedTotal || "";
    const currentCompleteOneYear = getValues("completeOneYear")?.trim() || "";
    if (nextCompleteOneYear && currentCompleteOneYear !== nextCompleteOneYear) {
      setValue("completeOneYear", nextCompleteOneYear, { shouldDirty: true });
    }
  }, [
    isDefaultForm,
    tuitionFee,
    admissionFee,
    otherExpenses,
    transfer,
    certificationEqualization,
    preparatoryDepartment,
    tuitionFeeVisible,
    admissionFeeVisible,
    otherExpensesVisible,
    transferVisible,
    certificationEqualizationVisible,
    preparatoryDepartmentVisible,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (!isDefaultForm) {
      setContent("");
      return;
    }

    const nextContent = buildShengenDefaultAdmissionBody({
      intake,
      feeValues: {
        tuitionFee,
        admissionFee,
        otherExpenses,
        transfer,
        certificationEqualization,
        preparatoryDepartment,
      },
      feeVisibility: {
        tuitionFee: !!tuitionFeeVisible,
        admissionFee: !!admissionFeeVisible,
        otherExpenses: !!otherExpensesVisible,
        transfer: !!transferVisible,
        certificationEqualization: !!certificationEqualizationVisible,
        preparatoryDepartment: !!preparatoryDepartmentVisible,
      },
      completeOneYear,
    });

    isSyncingFromInputs.current = true;
    setContent(nextContent);
  }, [
    isDefaultForm,
    intake,
    tuitionFee,
    admissionFee,
    otherExpenses,
    transfer,
    certificationEqualization,
    preparatoryDepartment,
    tuitionFeeVisible,
    admissionFeeVisible,
    otherExpensesVisible,
    transferVisible,
    certificationEqualizationVisible,
    preparatoryDepartmentVisible,
    completeOneYear,
  ]);

  const buildPayload = (bodyHtml: string): AOPShengenAdmissionData => {
    const values = getValues();
    const courseValue = joinSelectedCourse(values.degree, values.selectedCourse);
    return {
      currentDate: values.currentDate ? parseDate(values.currentDate) : "",
      fullName: values.fullName || "",
      dateOfBirth: values.dateOfBirth ? parseDate(values.dateOfBirth) : "",
      nationality: values.nationality || "",
      course: courseValue || "",
      userOffice: "01",
      whose_doc: values.whose_doc || "",
      title: values.signature || "Admission Letter",
      need_qr: !!values.withQR,
      duration: values.duration || "",
      mediumOfInstruction: values.mediumOfInstruction || "",
      coursePrefix: values.coursePrefix || "",
      body: splitHtmlByBlocks(bodyHtml),
    };
  };

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const taskReady = await generatedDoc.ensureTaskReadyForDocumentGenerate();
      if (!taskReady) return;
      const response = await admissionOfficeRequests.createShengenAdmissionDoc(dataForGeneration);
      generatedDoc.onDocumentGenerated(response);
    } catch {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const editorHtml = admissionEditorRef.current?.getContent() ?? content;
    if (editorHtml !== content) {
      setContent(editorHtml);
    }

    const payload = buildPayload(editorHtml);
    setDataForGeneration((prev) => ({ ...prev, ...payload }));
    setBodyForPrev((prev) => ({
      ...prev,
      ...payload,
      body: splitHtmlByBlocksWithFontConvert(editorHtml),
    }));

    const values = getValues();
    const courseValue = joinSelectedCourse(values.degree, values.selectedCourse);
    const ready =
      Boolean(values.fullName?.trim()) &&
      Boolean(values.nationality?.trim()) &&
      Boolean(courseValue) &&
      Boolean(values.signature?.trim()) &&
      editorHtml.trim().length > 0;

    if (ready) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    } else {
      setIsPreviewReady(false);
    }
  };

  const handleEditorChange = (newContent: string) => {
    setContent(newContent);
    if (isSyncingFromInputs.current) {
      isSyncingFromInputs.current = false;
    }
  };

  const handleCopy = async (fieldName: keyof IAOPFormValues) => {
    const value = getValues(fieldName);
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">ADMISSION/CONFIRMATION LETTER</p>
        <form className="admission-office-page-config-form">
          <AOPIdSourceRow
            control={control}
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            applyTaskPrefill={idSource.applyTaskPrefill}
            lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
            lastPrefilledStudentIdRef={idSource.lastPrefilledStudentIdRef}
            fieldEmail={!!fieldEmail}
            isTaskIdDisabled={idSource.isTaskIdDisabled}
            isStudentIdDisabled={idSource.isStudentIdDisabled}
            isFieldEmailDisabled={idSource.isFieldEmailDisabled}
            onClearTaskId={idSource.handleClearTaskId}
            onClearStudentId={idSource.handleClearStudentId}
            onClearUserEmail={idSource.handleClearUserEmail}
            onCheckUserEmail={() => void idSource.handleCheckUserEmail()}
            onCopy={idSource.handleCopyIdField}
          />
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.whose_doc}
                  inputProps={{
                    register: register("whose_doc"),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                    onArrowClick: whoseDocAuto.onArrowClick,
                  }}
                  identifier="whose"
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">Date of Issue</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name-nat">Full Name (Nat.)</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame-nat" id="full-mame-nat" type="text" {...register("fullNameNat")} />
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullNameNat")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">Data of Birth</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
              inputProps={{
                register: register("nationality", { required: true }),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
                onArrowClick: nationalitiesAuto.onArrowClick,
              }}
              identifier="nationality"
            />
          </div>
          <div className="form-group-fields form-group-fields-half">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Degree"
                value={degreeAuto.query}
                onChange={degreeAuto.onChange}
                onSelect={(v) => {
                  degreeAuto.onSelect(v);
                  setValue("degree", v);
                }}
                isOpen={degreeAuto.isOpen}
                options={degreeAuto.filtered}
                error={!!errors.degree}
                inputProps={{
                  register: register("degree"),
                  close: degreeAuto.close,
                  setOpen: degreeAuto.onClick,
                  onArrowClick: degreeAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "degree",
                }}
                identifier="malta-degree-auto"
              />
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">Selected course</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse")} />
            </div>
          </div>
          <div className="form-group-fields form-group-fields-half">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="category">Category</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="category" id="category" type="text" {...register("category")} />
            </div>
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Admission Semester"
                value={admissionSemesterDisplay}
                onChange={(v) => {
                  admissionSemesterAuto.onChange(v);
                  setValue("admissionSemester", v, { shouldDirty: true });
                }}
                onSelect={(v) => {
                  admissionSemesterAuto.onSelect(v);
                  setValue("admissionSemester", v, { shouldDirty: true });
                }}
                isOpen={admissionSemesterAuto.isOpen}
                options={admissionSemesterAuto.filtered}
                error={!!errors.admissionSemester}
                inputProps={{
                  register: register("admissionSemester"),
                  close: admissionSemesterAuto.close,
                  setOpen: admissionSemesterAuto.onClick,
                  onArrowClick: admissionSemesterAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "admissionSemester",
                }}
                identifier="admission-semester-auto"
              />
            </div>
          </div>
          <div className="form-group-fields form-group-fields-half">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Medium of Instruction"
                value={mediumOfInstructionAuto.query}
                onChange={mediumOfInstructionAuto.onChange}
                onSelect={(v) => {
                  mediumOfInstructionAuto.onSelect(v);
                  setValue("mediumOfInstruction", v);
                }}
                isOpen={mediumOfInstructionAuto.isOpen}
                options={mediumOfInstructionAuto.filtered}
                error={!!errors.mediumOfInstruction}
                inputProps={{
                  register: register("mediumOfInstruction"),
                  close: mediumOfInstructionAuto.close,
                  setOpen: mediumOfInstructionAuto.onClick,
                  onArrowClick: mediumOfInstructionAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "mediumOfInstruction",
                }}
                identifier="medium-of-instruction-auto"
              />
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">Duration</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Form of Education"
                value={formOfEducationAuto.query}
                onChange={formOfEducationAuto.onChange}
                onSelect={(v) => {
                  formOfEducationAuto.onSelect(v);
                  setValue("formOfEducation", v);
                }}
                isOpen={formOfEducationAuto.isOpen}
                options={formOfEducationAuto.filtered}
                error={!!errors.formOfEducation}
                clearButton
                inputProps={{
                  register: register("formOfEducation"),
                  close: formOfEducationAuto.close,
                  setOpen: formOfEducationAuto.onClick,
                  onArrowClick: formOfEducationAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "formOfEducation",
                }}
                identifier="form-of-education-auto"
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={maltaOfferedCourseAuto.query}
                onChange={maltaOfferedCourseAuto.onChange}
                onSelect={(v) => {
                  maltaOfferedCourseAuto.onSelect(v);
                  setValue("offeredCourse", v);
                  if (!v) {
                    clearMaltaOfferedCourseSelection(setValue);
                  }
                }}
                isOpen={maltaOfferedCourseAuto.isOpen}
                options={maltaOfferedCourseAuto.filtered}
                error={!!errors.offeredCourse}
                inputProps={{
                  register: register("offeredCourse"),
                  close: maltaOfferedCourseAuto.close,
                  setOpen: maltaOfferedCourseAuto.onClick,
                  onArrowClick: maltaOfferedCourseAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "offeredCourse",
                }}
                identifier="malta-offered-course-auto"
                clearButton
              />
            </div>
          </div>
          <div className="form-group-fields form-group-fields-half">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Intake"
                value={intakeAuto.query}
                onChange={intakeAuto.onChange}
                onSelect={(v) => {
                  intakeAuto.onSelect(v);
                  setValue("intake", v);
                }}
                isOpen={intakeAuto.isOpen}
                options={intakeAuto.filtered}
                error={!!errors.intake}
                allowAdd={true}
                onAddItem={(label) => {
                  const trimmed = label.trim();
                  if (!trimmed) return;
                  intakeAuto.onSelect(trimmed);
                  setValue("intake", trimmed);
                }}
                inputProps={{
                  register: register("intake"),
                  close: intakeAuto.close,
                  setOpen: intakeAuto.onClick,
                  onArrowClick: intakeAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "intake",
                }}
                identifier="malta-intake-auto"
              />
            </div>
            <MaltaFullTimeFeeField
              control={control}
              name="tuitionFee"
              visibilityName="tuitionFeeVisible"
              flagName="tuitionFeeFlag"
              flagLetter="S"
              flagAriaLabel="Tuition Fee scholarship flag"
              label="Tuition Fee"
              inputClassName="tuitionFee"
              inputId="tuitionFee"
            />
          </div>
          <div className="form-group-fields form-group-fields-half">
            <MaltaFullTimeFeeField
              control={control}
              name="acommodation"
              visibilityName="acommodationVisible"
              label="Acommodation"
              inputClassName="acommodation"
              inputId="acommodation"
            />
            <MaltaFullTimeFeeField
              control={control}
              name="medicalInsurance"
              visibilityName="medicalInsuranceVisible"
              label="Medical Insurance"
              inputClassName="medicalInsurance"
              inputId="medicalInsurance"
            />
          </div>
          <div className="form-group-fields form-group-fields-half">
            <MaltaFullTimeFeeField
              control={control}
              name="residencePermit"
              visibilityName="residencePermitVisible"
              label="Residence Permit"
              inputClassName="residencePermit"
              inputId="residencePermit"
            />
            <MaltaFullTimeFeeField
              control={control}
              name="admissionFee"
              visibilityName="admissionFeeVisible"
              label="Admission Fee"
              inputClassName="admissionFee"
              inputId="admissionFee"
            />
          </div>
          <div className="form-group-fields form-group-fields-half">
            <MaltaFullTimeFeeField
              control={control}
              name="otherExpenses"
              visibilityName="otherExpensesVisible"
              label="Other Expenses"
              inputClassName="otherExpenses"
              inputId="otherExpenses"
            />
            <MaltaFullTimeFeeField
              control={control}
              name="transfer"
              visibilityName="transferVisible"
              label="Transfer"
              inputClassName="transfer"
              inputId="transfer"
            />
          </div>
          <div className="form-group-fields form-group-fields-full">
            <MaltaFullTimeFeeField
              control={control}
              name="certificationEqualization"
              visibilityName="certificationEqualizationVisible"
              label="Certification & Equalization"
              inputClassName="certificationEqualization"
              inputId="certificationEqualization"
            />
          </div>
          <div className="form-group-fields form-group-fields-full">
            <MaltaFullTimeFeeField
              control={control}
              name="preparatoryDepartment"
              visibilityName="preparatoryDepartmentVisible"
              label="Preparatory Department"
              inputClassName="preparatoryDepartment"
              inputId="preparatoryDepartment"
              disabled={!isPreparatoryDepartmentActive}
            />
          </div>
          <div className="form-group-fields form-group-fields-complete-one-year-row">
            <MaltaFullTimeFeeField
              control={control}
              name="completeOneYear"
              label="Complete One Year"
              inputClassName="completeOneYear"
              inputId="completeOneYear"
              readOnly
              hideVisibilityToggle
            />
            <div className="form-group-fields-second-year">
              <Controller
                name="showSecondYear"
                control={control}
                render={({ field }) => <CustomSwitch label="Second year" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
          <label>Admission body</label>
          <Editor
            key={`admission-${formOfEducation || "empty"}`}
            value={content}
            onInit={(_evt, editor) => {
              admissionEditorRef.current = editor;
            }}
            init={{
              height: TINYMCE_EDITOR_HEIGHT,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar:
                "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              font_size_formats: TINYMCE_FONT_SIZE_FORMATS,
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: TINYMCE_EDITOR_CONTENT_STYLE,
            }}
            onEditorChange={handleEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />

          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields form-group-fields-signature-row">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Admission Letter"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                clearButton
                inputProps={{
                  register: register("signature", { required: true }),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                  onArrowClick: signatureAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "signature",
                }}
                identifier="signature"
              />
            </div>
            <div className="form-group-fields-invoice">
              <Controller
                name="withQR"
                control={control}
                render={({ field }) => <CustomSwitch label="With QR" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
